import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { ApiError } from '@rydres/api-client';
import { BRAND, tcpLabel } from '@rydres/shared';
import { Button, Notice, Spinner, fmtDateTime, fmtPhone, formatUsd } from '@rydres/ui';
import { api, STRIPE_PUBLISHABLE_KEY } from '../lib/api.js';
import type { PublicBookingView } from '../lib/types.js';

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

/** rydres.com/q/<token>: the rider sees the driver's price and accepts it. */
export function QuotePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [view, setView] = useState<PublicBookingView | null | undefined>(undefined);
  const [paying, setPaying] = useState<PublicBookingView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.call<PublicBookingView>('GET', `/public/quotes/${token}`, { auth: false }).then(setView, () => setView(null));
  }, [token]);
  if (view === undefined) return <div className="flex min-h-screen items-center justify-center"><Spinner /></div>;
  if (view === null) return <div className="p-6 text-muted">We could not find that quote.</div>;
  const { booking: b, driver: d } = view;
  const expired = b.status === 'expired' || (b.quoteExpiresAt && new Date(b.quoteExpiresAt).getTime() < Date.now());

  const accept = async (paymentMode: 'card' | 'direct') => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.call<PublicBookingView>('POST', `/public/quotes/${token}/accept`, { auth: false, body: { paymentMode } });
      if (r.clientSecret) setPaying(r);
      else navigate(`/b/${r.booking.manageToken}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 pb-16 pt-8">
      <h1 className="text-2xl font-black">Your quote from {d.displayName}</h1>
      <p className="mt-1 text-sm text-ink2">{d.legalName} · {tcpLabel(d.tcpNumber)}{d.handle && <> · <Link className="font-bold text-accent-text underline" to={`/me/drivers/${d.handle}`}>About {d.displayName ?? 'your driver'}</Link></>}</p>
      <div className="mt-6 rounded-2xl border border-line bg-card p-4">
        <div className="text-3xl font-black">{b.priceCents != null ? formatUsd(b.priceCents) : '—'}</div>
        <div className="text-xs text-muted">per vehicle, all in</div>
        <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted">Pickup</dt><dd className="font-bold">{fmtDateTime(b.startsAt)}<div className="font-medium text-ink2">{b.pickupAddress}</div></dd>
          <dt className="text-muted">Drop-off</dt><dd>{b.dropoffAddress}</dd>
          <dt className="text-muted">Passengers</dt><dd>{b.passengers}</dd>
          {b.notes && <><dt className="text-muted">Your request</dt><dd className="whitespace-pre-line text-ink2">{b.notes}</dd></>}
        </dl>
      </div>
      {b.status === 'confirmed' || b.status === 'completed' ? (
        <div className="mt-4"><Notice tone="ok">This trip is confirmed. <a className="underline" href={`/b/${b.manageToken}`}>See the details</a>.</Notice></div>
      ) : expired ? (
        <div className="mt-4"><Notice tone="warn">This quote has expired. Call {fmtPhone(d.phone)} or <a className="underline" href={`/${d.handle}`}>ask again</a>.</Notice></div>
      ) : b.status !== 'quoted' ? (
        <div className="mt-4"><Notice tone="warn">This quote is no longer open ({b.status}).</Notice></div>
      ) : paying && stripePromise ? (
        <div className="mt-6">
          <Elements stripe={stripePromise} options={{ clientSecret: paying.clientSecret!, appearance: { theme: 'stripe', variables: { colorPrimary: '#2563eb', borderRadius: '12px' } } }}>
            <PayForm view={paying} />
          </Elements>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {b.quoteExpiresAt && <p className="text-xs text-muted">Good until {fmtDateTime(b.quoteExpiresAt)}. {d.cancelPolicyText}</p>}
          {stripePromise && <Button className="w-full" disabled={busy} onClick={() => void accept('card')}>Accept and pay by card</Button>}
          <Button variant="outline" className="w-full" disabled={busy} onClick={() => void accept('direct')}>Accept, pay the driver directly</Button>
          {error && <Notice tone="danger">{error}</Notice>}
          <p className="text-xs text-muted">Your contract is with {d.legalName ?? d.displayName}, a licensed California charter-party carrier. {BRAND.name} provides the booking software only.</p>
        </div>
      )}
    </div>
  );
}

function PayForm({ view }: { view: PublicBookingView }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    const { error: err } = await stripe.confirmPayment({ elements, confirmParams: { return_url: `${location.origin}/b/${view.booking.manageToken}?paid=1` } });
    if (err) {
      setError(err.message ?? 'Payment did not go through.');
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      {error && <Notice tone="danger">{error}</Notice>}
      <Button className="w-full" disabled={busy || !stripe} onClick={() => void pay()}>{busy ? 'Processing…' : `Pay ${formatUsd(view.booking.priceCents ?? 0)}`}</Button>
    </div>
  );
}
